import {
    ContainerOutlined,
    ControlOutlined,
    PartitionOutlined,
    PieChartOutlined,
    SearchOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { getText, Tlt, useAPI, useLanguage } from "@clairejs/react";
import { useNavigator } from "@clairejs/react-web";
import { Menu, type MenuProps } from "antd";
import { useEffect, useMemo, useState } from "react";

import { flat } from "../../utils/data";
import { useStore } from "../../hooks/system";
import { UserApi } from "../../store/api/user";

type MenuItem = Required<MenuProps>["items"][number] & { children?: MenuItem[]; key: string; permissions?: string[] };

function getItem(
    label: () => React.ReactNode,
    key: string,
    permissions?: string[],
    icon?: React.ReactNode,
    children?: MenuItem[],
) {
    return {
        key,
        icon,
        children,
        label: label(),
        permissions,
    } as MenuItem;
}

const menuItems = () => [
    getItem(() => getText(<Tlt template="Dashboard" />), "/admin/dashboard", undefined, <PieChartOutlined />),
    getItem(() => getText(<Tlt template="Referal" />), "/admin/referal", undefined, <PartitionOutlined />, [
        getItem(() => getText(<Tlt template="Revenue" />), "/admin/referal/revenue"),
        getItem(() => getText(<Tlt template="Settings" />), "/admin/referal/code"),
        getItem(() => getText(<Tlt template="Users" />), "/admin/referal/users"),
    ]),
    getItem(() => getText(<Tlt template="Searches" />), "/admin/search", [], <SearchOutlined />, [
        getItem(() => getText(<Tlt template="Searches" />), "/admin/searches"),
        getItem(() => getText(<Tlt template="Configs" />), "/admin/search-configs"),
    ]),
    getItem(() => getText(<Tlt template="Payments" />), "/admin/payments", [], <ShoppingCartOutlined />, [
        getItem(() => getText(<Tlt template="Cashouts" />), "/admin/payments/cashouts"),
        getItem(() => getText(<Tlt template="Payments" />), "/admin/payments/list"),
    ]),
    getItem(() => getText(<Tlt template="Accounts" />), "/admin/accounts", [], <ShopOutlined />, [
        getItem(() => getText(<Tlt template="Accounts" />), "/admin/payments/accounts"),
        getItem(() => getText(<Tlt template="Currencies" />), "/admin/payments/currencies", []),
    ]),
    getItem(() => getText(<Tlt template="Users" />), "/admin/users", [], <TeamOutlined />, [
        getItem(() => getText(<Tlt template="Users list" />), "/admin/data/users/list"),
        getItem(() => getText(<Tlt template="Users map" />), "/admin/data/users/map"),
    ]),
    getItem(() => getText(<Tlt template="Data" />), "/admin/data", [], <ContainerOutlined />, [
        getItem(() => getText(<Tlt template="Services" />), "/admin/data/services"),
        getItem(() => getText(<Tlt template="Countries" />), "/admin/data/countries"),
    ]),
    getItem(() => getText(<Tlt template="System" />), "/admin/system", [], <ControlOutlined />, [
        getItem(() => getText(<Tlt template="Announcements" />), "/admin/system/announces"),
        getItem(() => getText(<Tlt template="Settings" />), "/admin/system/settings"),
    ]),
];

export default function AppMenu() {
    const navigator = useNavigator();
    const [lang] = useLanguage();

    const path = navigator.getPath();

    const me = useStore((s) => s.me?.info?.id);
    const api = useAPI(UserApi);

    const [isAdmin, setIsAdmin] = useState<boolean>();
    const [permissions, setPermissions] = useState<string[]>();

    const items = useMemo(() => menuItems(), [lang]);
    const flatItems = useMemo(() => flat(items, (item) => item.children), [items]);
    const defautlSelectedKeys = useMemo(
        () => flatItems.filter((item) => item.key && path.startsWith(item.key)).map((item) => item.key),
        [path, flatItems],
    );

    const fetchPermissions = async () => {
        const permissions = await api.getPermissions();

        setIsAdmin(permissions?.isSuperUser);
        setPermissions(permissions?.permissions.map((p) => p.permissionId));
    };

    useEffect(() => {
        if (me) {
            fetchPermissions();
        }
    }, [me]);

    return (
        <Menu
            theme="dark"
            mode="inline"
            defaultSelectedKeys={defautlSelectedKeys}
            defaultOpenKeys={defautlSelectedKeys}
            items={items.filter(
                (item) => !item.permissions || isAdmin || item.permissions.some((p) => permissions?.includes(p)),
            )}
            onSelect={(item) => {
                navigator.navigate(item.key);
            }}
        />
    );
}
